import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import {
    type PracticeLocation,
    practiceLocationDetailsSchema,
    useAddPracticeLocationOptions,
    useUpdatePracticeLocationOptions,
} from '@/entities/practice';

import { DEFAULT_PLACE_OF_SERVICE_CODE } from '../config/save-location.config';

export type SaveLocationFormValues = z.input<typeof practiceLocationDetailsSchema>;

type Options = {
    // Present in edit mode, prefilling the form and routing the submit through
    // `updatePracticeLocation` instead of `addPracticeLocation`.
    location?: PracticeLocation;
    onSuccess: (name: string) => void;
};

function toFormValues(location: PracticeLocation): SaveLocationFormValues {
    return {
        name: location.name,
        street1: location.street1,
        street2: location.street2 ?? '',
        city: location.city,
        state: location.state,
        zip: location.zip,
        placeOfServiceCode: location.placeOfServiceCode,
        offersTelehealth: location.offersTelehealth,
        defaultAppointmentModality: location.defaultAppointmentModality,
    };
}

export function useSaveLocationForm({ location, onSuccess }: Options) {
    const addLocation = useMutation(useAddPracticeLocationOptions());
    const updateLocation = useMutation(useUpdatePracticeLocationOptions());

    const form = useForm<
        SaveLocationFormValues,
        unknown,
        z.output<typeof practiceLocationDetailsSchema>
    >({
        resolver: zodResolver(practiceLocationDetailsSchema),
        // `offersTelehealth` starts unanswered in add mode (undefined, not a default of `false`),
        // so leaving it untouched refuses the save instead of silently storing a no answer.
        defaultValues: location
            ? toFormValues(location)
            : {
                  name: '',
                  street1: '',
                  street2: '',
                  city: '',
                  zip: '',
                  placeOfServiceCode: DEFAULT_PLACE_OF_SERVICE_CODE,
                  defaultAppointmentModality: null,
              },
    });

    const onSubmit = form.handleSubmit((data) => {
        const onError = () => {
            form.setError('root', { message: "Couldn't save. Try again." });
        };
        if (location) {
            updateLocation.mutate(
                { locationId: location.id, ...data },
                { onSuccess: () => onSuccess(data.name), onError },
            );
        } else {
            addLocation.mutate(data, { onSuccess: () => onSuccess(data.name), onError });
        }
    });

    // Switching the telehealth answer to No discards any stored default modality — a location
    // answered No never carries one.
    const setOffersTelehealth = (value: boolean) => {
        form.setValue('offersTelehealth', value, { shouldValidate: true });
        if (!value) {
            form.setValue('defaultAppointmentModality', null, { shouldValidate: true });
        }
    };

    return {
        form,
        onSubmit,
        setOffersTelehealth,
        isPending: addLocation.isPending || updateLocation.isPending,
    };
}
